import React, { useEffect, useRef } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import './ConfirmationModal.css';

/**
 * ConfirmationModal — Reusable confirmation / alert popup
 *
 * Props:
 *   isOpen      — boolean, controls visibility
 *   title       — string, modal heading
 *   message     — string, body text
 *   ctaLabel    — string, primary button label (default: "Continue")
 *   onConfirm   — () => void, called on CTA click
 *   onCancel    — () => void, optional — if provided, shows a Cancel button
 *   cancelLabel — string, cancel button label (default: "Cancel")
 *   icon        — string | null, optional emoji/icon to show above title
 *   variant     — 'default' | 'danger' — danger uses red CTA
 *   ctaDisabled — boolean, optional (default false) — disables the CTA button
 *                 without hiding it; used e.g. when a caller requires a field
 *                 (a typed reason) to be filled before confirming.
 *   autoFocusCancel — boolean, optional (default false) — puts the initial
 *                 focus on the CANCEL (keep) button instead of leaving it on
 *                 whatever opened the modal. Used by destructive confirms
 *                 (deleting a template, D16) so a stray Enter keeps the row.
 *   children    — optional extra content rendered between the message and
 *                 the action buttons (e.g. the FIX-5 discharge-stamp
 *                 Planned-vs-Now radio choice). Absent by default — every
 *                 existing plain-text confirm is unaffected.
 */
const ConfirmationModal = ({
  isOpen,
  title,
  message,
  ctaLabel = 'Continue',
  onConfirm,
  onCancel,
  cancelLabel = 'Cancel',
  icon = '✅',
  variant = 'default',
  ctaDisabled = false,
  autoFocusCancel = false,
  children,
}) => {
  const { theme, isDark } = useTheme();
  const cancelRef = useRef(null);

  useEffect(() => {
    if (isOpen && autoFocusCancel) cancelRef.current?.focus();
  }, [isOpen, autoFocusCancel]);

  if (!isOpen) return null;

  const dangerGradient = 'linear-gradient(135deg, #dc2626, #ef4444)';

  return (
    <div
      className="confirmation-overlay"
      style={{ backgroundColor: theme.background.overlay || 'rgba(0,0,0,0.5)' }}
      onClick={onCancel || undefined}
    >
      <div
        className="confirmation-modal"
        style={{
          backgroundColor: theme.background.primary,
          color: theme.text.primary,
          borderColor: theme.border.input,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {icon && <div className="confirmation-icon">{icon}</div>}

        <h2 className="confirmation-title">{title}</h2>
        <p className="confirmation-message" style={{ color: theme.text.secondary }}>
          {message}
        </p>

        {children}

        <div className={onCancel ? 'confirmation-actions' : undefined}>
          {onCancel && (
            <button
              ref={cancelRef}
              className="confirmation-cancel"
              onClick={onCancel}
              style={{
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: theme.text.secondary,
              }}
            >
              {cancelLabel}
            </button>
          )}
          <button
            className="confirmation-cta"
            onClick={onConfirm}
            disabled={ctaDisabled}
            style={{
              background: variant === 'danger' ? dangerGradient : theme.gradient.userMessage,
              color: '#fff',
              opacity: ctaDisabled ? 0.6 : 1,
              cursor: ctaDisabled ? 'default' : 'pointer',
            }}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
